import React from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Handshake,
  Code2,
  Users,
  Boxes,
  Building2,
  Cpu,
  Headphones,
  Layers,
  Plug,
  MessageSquare,
  Search,
  Rocket,
  LifeBuoy,
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

const HERO_IMAGE =
  'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/zi2fkc36_PARTNER%20PAGE%20BACKDROP.png';

/* ─────────────────────────────── data ─────────────────────────────── */
const PARTNERSHIP_TYPES = [
  {
    icon: Code2,
    title: 'Technology Partners',
    description: "Integrate eCOMM's payment technologies into your software, platform or commerce solution through flexible APIs and developer tools.",
  },
  {
    icon: Users,
    title: 'Referral & Reseller Partners',
    description: "Introduce merchants to eCOMM's payment solutions and benefit from collaborative commercial opportunities and dedicated support.",
  },
  {
    icon: Boxes,
    title: 'EPOS & Integration Partners',
    description: 'Combine payment acceptance with EPOS, hospitality and retail technologies through integrated commerce solutions.',
  },
  {
    icon: Building2,
    title: 'Enterprise Partnerships',
    description: 'Work with eCOMM on tailored payment solutions designed for larger or specialist operational environments.',
  },
];

const WHY_PARTNER = [
  { icon: Cpu,        title: 'Flexible Payment Technology', body: 'Access modern payment solutions spanning in-store, online, mobile and unattended payment environments.' },
  { icon: Headphones, title: 'Dedicated Support',           body: 'Work closely with experienced technical, operational and commercial teams throughout onboarding and implementation.' },
  { icon: Layers,     title: 'Scalable Infrastructure',     body: 'Support businesses of all sizes through secure and reliable payment technologies.' },
  { icon: Plug,       title: 'API & Integration Capabilities', body: 'Build flexible payment experiences through hosted checkout, APIs and gateway integration options.' },
];

const PROCESS_STEPS = [
  { icon: MessageSquare, label: 'Step 1', title: 'Discover',  body: 'Discuss your business requirements.' },
  { icon: Search,        label: 'Step 2', title: 'Evaluate',  body: 'Explore technical and commercial fit.' },
  { icon: Rocket,        label: 'Step 3', title: 'Implement', body: 'Implementation & onboarding.' },
  { icon: LifeBuoy,      label: 'Step 4', title: 'Launch',    body: 'Launch & ongoing support.' },
];

/* ─────────────────────────────── small components ─────────────────────────────── */
const Eyebrow = ({ children, testId }) => (
  <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.22em] mb-3" data-testid={testId}>{children}</p>
);

const SectionHeading = ({ eyebrow, title, accent = true, light = false }) => (
  <div className="mb-8">
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className={`text-3xl sm:text-4xl lg:text-[2.75rem] font-bold leading-tight tracking-tight ${light ? 'text-white' : 'text-[#0a1929]'}`}>{title}</h2>
    {accent && <div className="h-1 w-20 bg-[#ef5b25] rounded-full mt-4" />}
  </div>
);

/* ─────────────────────────────── page ─────────────────────────────── */
const AboutPartner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromTopNav = location.state?.from === 'top-nav';

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="about-partner-page">
      <Header />
      <div className="h-20" />

      {/* ╔═════════════════════ HERO ═════════════════════╗ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f7f9fc] via-white to-[#eef3f8]" data-testid="partner-hero">
        <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#1F4E79]/8 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-40 -left-32 w-96 h-96 rounded-full bg-[#ef5b25]/8 blur-3xl" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 sm:pt-16 sm:pb-24">
          <button
            onClick={() => (fromTopNav ? navigate('/') : navigate(-1))}
            className="flex items-center gap-2 text-gray-600 hover:text-[#1F4E79] font-medium mb-10 transition-colors duration-200"
            data-testid="partner-back-button"
          >
            <ArrowLeft size={18} />
            {fromTopNav ? 'Back to Home' : 'Back'}
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-white/80 via-white/95 to-white/80 backdrop-blur-md border border-white/80 text-[#1F4E79] text-[11px] font-bold uppercase tracking-[0.22em] mb-6 shadow-[0_4px_20px_-2px_rgba(31,78,121,0.18)] ring-1 ring-inset ring-white/60"
                data-testid="partner-hero-chip"
              >
                <Handshake size={13} className="text-[#ef5b25]" strokeWidth={2} />
                Partner With Us
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[64px] font-bold text-[#0a1929] leading-[1.05] tracking-tight mb-6" data-testid="partner-title">
                Partner With<br />
                <span className="text-[#1F4E79]">eCOMM.</span> Grow Together.
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl mb-10 font-medium">
                Partner with eCOMM to deliver innovative payment solutions backed by flexible technology, dedicated support and a commitment to long-term growth. We work closely with our partners to create value, expand opportunities and help businesses thrive.
              </p>
              <div className="flex">
                <Link
                  to="/contact-sales"
                  onClick={() => window.__resetSourceScroll?.()}
                  className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg"
                  data-testid="partner-cta-contact"
                >
                  Speak to our Team</Link>
              </div>
            </div>

            {/* Handshake image card */}
            <div className="lg:col-span-5">
              <div className="relative max-w-md mx-auto lg:mx-0" data-testid="partner-hero-image">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#1F4E79]/15 to-[#ef5b25]/15 rounded-3xl blur-2xl pointer-events-none" aria-hidden="true" />
                <div className="relative rounded-2xl overflow-hidden border border-white/60 shadow-2xl">
                  <img
                    src={HERO_IMAGE}
                    alt="Two business partners shaking hands"
                    className="w-full h-auto object-cover aspect-square"
                  />
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0a1929]/90 via-[#0a1929]/55 to-transparent p-5 pt-14">
                    <p className="text-[10px] font-bold text-[#ffcba6] uppercase tracking-[0.2em] mb-1">Commercial Partnerships</p>
                    <p className="text-white font-bold text-xl leading-tight">Stronger Together.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ INTRO ═════════════════════╗ */}
      <section className="bg-white py-20 sm:py-24" data-testid="partner-intro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Building Strong Commercial Partnerships" title="Designed to support long-term growth." />
          </div>
          <div className="lg:col-span-7 space-y-5 text-gray-700 leading-relaxed text-base sm:text-lg">
            <p>
              eCOMM works with businesses, technology providers and commercial partners to deliver secure, scalable payment solutions across multiple industries.
            </p>
            <p>
              Whether you're an independent software vendor, reseller, EPOS provider or referral partner, we offer flexible partnership opportunities designed to support long-term growth.
            </p>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ PARTNERSHIP TYPES ═════════════════════╗ */}
      <section className="bg-[#f7f9fc] py-20 sm:py-24" data-testid="partnership-types">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <SectionHeading eyebrow="Partnership Types" title="Work with eCOMM." />
            <p className="text-lg text-gray-700 leading-relaxed">
              Pick the partnership model that best aligns with your business — from deep technical integration to commercial referral relationships.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5">
            {PARTNERSHIP_TYPES.map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="group relative rounded-2xl bg-gradient-to-br from-white via-white to-[#f0f5fa] border border-gray-100 shadow-[0_10px_30px_-8px_rgba(31,78,121,0.18)] hover:shadow-[0_18px_38px_-8px_rgba(31,78,121,0.28)] hover:-translate-y-1 hover:border-[#1F4E79]/25 p-6 sm:p-7 transition-all duration-300 overflow-hidden"
                  data-testid={`partnership-card-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#1F4E79]/10 to-[#1F4E79]/5 group-hover:from-[#1F4E79]/15 group-hover:to-[#1F4E79]/10 flex items-center justify-center transition-colors duration-200">
                      <Icon size={22} className="text-[#1F4E79]" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-[#0a1929] text-lg leading-snug mb-2">{item.title}</h3>
                      <p className="text-[14px] text-gray-600 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ WHY PARTNER ═════════════════════╗ */}
      <section className="bg-gradient-to-br from-[#0a1929] via-[#0e2540] to-[#163a5f] py-20 sm:py-24 relative overflow-hidden" data-testid="why-partner">
        <div className="pointer-events-none absolute -top-32 right-0 w-96 h-96 rounded-full bg-[#ef5b25]/10 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#1F4E79]/30 blur-3xl" aria-hidden="true" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <p className="text-[11px] font-bold text-[#ffcba6] uppercase tracking-[0.22em] mb-3">Why Partner With eCOMM</p>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-white leading-tight tracking-tight mb-4">Built to Help You Grow.</h2>
            <div className="h-1 w-20 bg-[#ef5b25] rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY_PARTNER.map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="rounded-xl bg-white/10 backdrop-blur-md border border-white/15 p-6 hover:bg-white/15 transition-all duration-200"
                  data-testid={`why-partner-card-${i}`}
                >
                  <div className="w-11 h-11 rounded-lg bg-[#ef5b25]/20 border border-[#ef5b25]/30 flex items-center justify-center mb-4">
                    <Icon size={20} className="text-[#ffcba6]" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-white font-bold text-[15px] mb-2 leading-tight">{item.title}</h3>
                  <p className="text-white/80 text-[13.5px] leading-relaxed">{item.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ PARTNERSHIP PROCESS ═════════════════════╗ */}
      <section className="bg-white py-20 sm:py-24" data-testid="partnership-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <Eyebrow>Partnership Process</Eyebrow>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-[#0a1929] leading-tight tracking-tight mb-4">A Simple, Structured Journey.</h2>
            <div className="h-1 w-20 bg-[#ef5b25] rounded-full mx-auto mb-5" />
            <p className="text-lg text-gray-700 leading-relaxed">From first conversation to launch and beyond — partnering with eCOMM follows a clear, repeatable path.</p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Connector line — desktop only */}
            <div className="hidden lg:block absolute top-7 left-[12%] right-[12%] h-px bg-gradient-to-r from-[#1F4E79]/15 via-[#1F4E79]/40 to-[#1F4E79]/15" aria-hidden="true" />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {PROCESS_STEPS.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div key={i} className="relative flex flex-col items-center text-center" data-testid={`process-step-${i}`}>
                    <div className="relative z-10 w-14 h-14 rounded-full bg-gradient-to-br from-[#1F4E79] to-[#0a1929] flex items-center justify-center shadow-lg ring-4 ring-white">
                      <Icon size={22} className="text-white" strokeWidth={1.75} />
                    </div>
                    <p className="mt-5 text-[10px] font-bold text-[#ef5b25] uppercase tracking-[0.2em] mb-1">{step.label}</p>
                    <h3 className="font-bold text-[#0a1929] text-lg leading-tight mb-2">{step.title}</h3>
                    <p className="text-[14px] text-gray-600 leading-relaxed max-w-[260px]">{step.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ CTA STRIP ═════════════════════╗ */}
      <section id="contact" className="py-16 sm:py-20 bg-[#f7f9fc]" data-testid="partner-final-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-gradient-to-br from-[#0a1929] via-[#0e2540] to-[#163a5f] p-8 sm:p-14 text-center shadow-xl relative overflow-hidden">
            <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#ef5b25]/15 blur-3xl" aria-hidden="true" />
            <div className="pointer-events-none absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#1F4E79]/25 blur-3xl" aria-hidden="true" />
            <div className="relative">
              <h3 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold text-white leading-tight tracking-tight mb-4">
                Let's Build Together.
              </h3>
              <p className="text-white/90 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
                Speak with our partnerships team to explore how eCOMM can support your business, platform or customer base.
              </p>
              <div className="flex justify-center">
                <Link
                  to="/contact-sales"
                  onClick={() => window.__resetSourceScroll?.()}
                  className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-white text-[#1F4E79] font-bold rounded-lg hover:bg-white/90 hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg"
                  data-testid="partner-cta-final"
                >
                  Contact Us</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default AboutPartner;
